import get from "lodash/get";
import React, { useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";
import { Waypoint } from "react-waypoint";

import Preloader from "~/components/Preloader";
import Input from "~/shared/ui/Fields/Input";
import Select from "~/shared/ui/Fields/Select";
import { useDealerActions } from "~/app/Context/DealerContext";
import { useDealersForSelect } from "~/entity/Dealers/hooks/useDealersForSelect";
import { useSetLocationData } from "~/hooks/locationData";
import {
  GetCurrentDealerContext,
  GetCurrentDealerContextPrpos,
} from "~/pages/IvideonDealerRegistry/getCurrentDealer";
import {
  useGetDealersFederalAreaQuery,
  useGetDealersRegionsQuery,
} from "~/schemas/generated/graphql";

import s from "./SelectDealerForm.less";

const SelectDealerForm = ({ onSelect }: { onSelect?: () => void }) => {
  const { t } = useTranslation();
  const isEn = localStorage.getItem("language") === "en";
  const { handleCurrentDealer } = useContext<GetCurrentDealerContextPrpos>(
    GetCurrentDealerContext,
  );

  const { setDealerIdToContext } = useDealerActions();
  const setData = useSetLocationData();
  const containerRef = useRef(null);
  const { control, reset, watch } = useForm();
  const dealersFederalAreaQuery = useGetDealersFederalAreaQuery({});
  const dealersRegionsQuery = useGetDealersRegionsQuery({});

  const {
    options: dealersForSelect,
    loading,
    onScrollBottom: handleScrollBottom,
    searchDealers,
    setFederalAreaFilter,
    setRegionFilter,
  } = useDealersForSelect();

  const dealersFederalArea = get(
    dealersFederalAreaQuery,
    `data.DealerGetter.getDealersFederalArea.items${isEn ? "En" : ""}`,
    [],
  );
  const dealersRegions = get(
    dealersRegionsQuery,
    `data.DealerGetter.getDealersRegions.items${isEn ? "En" : ""}`,
    [],
  );

  const partName = watch("partName") || "";
  const federalArea = watch("FederalArea");
  const region = watch("Region");

  useEffect(() => {
    searchDealers(partName);
  }, [partName, searchDealers]);

  useEffect(() => {
    const value = federalArea?.value || "";
    setFederalAreaFilter(value, isEn);
  }, [federalArea, isEn, setFederalAreaFilter]);

  useEffect(() => {
    const value = region?.value || "";
    setRegionFilter(value, isEn);
  }, [region, isEn, setRegionFilter]);

  useEffect(() => {
    if (!dealersFederalAreaQuery.loading && dealersFederalArea.length > 0) {
      reset(
        { FederalArea: dealersFederalArea[0] },
        { keepDefaultValues: true },
      );
    }
    if (!dealersRegionsQuery.loading && dealersRegions.length > 0) {
      reset({ Region: dealersRegions[0] }, { keepDefaultValues: true });
    }
  }, [
    dealersFederalArea,
    dealersRegions,
    reset,
    dealersFederalAreaQuery.loading,
    dealersRegionsQuery.loading,
  ]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollBottom = 80;
    }
  }, [containerRef.current]);

  const setDealerId = (dealerName, dealerNameEn, id) => {
    sessionStorage.setItem("currentDealerId", id);
    sessionStorage.setItem("currentDealerName", dealerName);
    sessionStorage.setItem("currentDealerNameEn", dealerNameEn);
    setData("openModal", "");
    handleCurrentDealer({
      DealerName: dealerName,
      DealerNameEn: dealerNameEn,
      id,
    });
    // handleCurrentDealer({ DealerName, DealerNameEn, id });
    setDealerIdToContext(id);
    onSelect?.();
  };

  return (
    <div className={s.select_dealer_wrap}>
      <Input
        name="partName"
        search
        placeholder={t("ui.search")}
        control={control}
      />
      <Select
        name="FederalArea"
        customClassName={s.dealer_form_select}
        options={["place.federalArea", ...dealersFederalArea]}
        control={control}
        loading={dealersFederalAreaQuery.loading}
      />
      <Select
        name="Region"
        customClassName={s.dealer_form_select}
        options={["place.region", ...dealersRegions]}
        control={control}
        loading={dealersRegionsQuery.loading}
      />
      <div className={s.deaelers_container} ref={containerRef}>
        {dealersForSelect.map(
          ({ label: dealerName, value: id, dealerNameEn }) => (
            <div
              className={s.dealer_name}
              key={id}
              data-dealer_id={id}
              onClick={() =>
                setDealerId(dealerName, dealerNameEn || dealerName, id)
              }
            >
              {isEn ? dealerNameEn || dealerName : dealerName}
            </div>
          ),
        )}
        {loading && <Preloader />}
        {dealersForSelect.length > 24 && (
          <Waypoint onEnter={handleScrollBottom} />
        )}
      </div>
    </div>
  );
};

export default SelectDealerForm;
